import {
  AttributionType,
  AttributionTypeConst,
  DateRange,
  DateRangePresetConst,
  HierarchyLevel,
  HierarchyLevelConst
} from './dtc-commerce-mmm-types';

// Run-discovery filters shared by both surfaces. The model type itself varies per variant —
// see `modelType` on MmmDashboardConfig.
//
// Production runs only. Non-main branches are dev/experiment runs.
export const MMM_RUN_BRANCH = 'main';

// Official Gemini wordmark (same asset CopyTiger uses), shown in the MMM Agent panel header.
export const GEMINI_LOGO_URL = 'https://cdn.blisspointmedia.com/assets/img/Gemini.png';
// A lower bound, not an equality check: the gate exists to skip pre-Meridian runs (version 1),
// and `=== 2` would also drop a future version 3. Matches the HROAS resolver's `>= 2`.
export const MMM_MIN_OUTPUT_FILE_VERSION = 2;

// A channel is "dark" when it accounts for less than this share of total spend (as a
// percentage) in the selected window — i.e. negligible current investment, not strictly zero.
// Dark channels are hidden by default in both the bar and table views, behind a toggle.
export const DARK_CHANNEL_SPEND_SHARE_PCT = 1;

export const DEFAULT_ATTRIBUTION: AttributionType = AttributionTypeConst.ACTUALIZED;
export const DEFAULT_HIERARCHY: HierarchyLevel = HierarchyLevelConst.CHANNEL;

export const DEFAULT_DATE_RANGE: DateRange = {
  preset: DateRangePresetConst.T12M,
  startDate: '',
  endDate: '',
  label: 'Trailing 12 months'
};

export const DATE_PRESET_OPTIONS: {
  key: (typeof DateRangePresetConst)[keyof typeof DateRangePresetConst];
  label: string;
}[] = [
  { key: DateRangePresetConst.T3M, label: 'T3M' },
  { key: DateRangePresetConst.T6M, label: 'T6M' },
  { key: DateRangePresetConst.T12M, label: 'T12M' },
  { key: DateRangePresetConst.YTD, label: 'YTD' },
  { key: DateRangePresetConst.ALL, label: 'All time' }
];

export const ATTRIBUTION_OPTIONS: { key: AttributionType; label: string }[] = [
  { key: AttributionTypeConst.ACTUALIZED, label: 'Actualized' },
  { key: AttributionTypeConst.RAW, label: 'Direct' }
];

export const HIERARCHY_OPTIONS: { value: HierarchyLevel; label: string }[] = [
  { value: HierarchyLevelConst.CHANNEL, label: 'Channel' },
  { value: HierarchyLevelConst.PLATFORM, label: 'Channel / Platform' },
  {
    value: HierarchyLevelConst.BRAND_NONBRAND,
    label: 'Channel / Platform / Tactic / Brand-nonbrand'
  }
];

export const BASELINE_GROUP_KEY = '__baseline__';
export const BASELINE_DISPLAY_NAME = 'Baseline / Organic';
export const BASELINE_TOGGLE_LABEL = 'Show baseline / organic as a tracked channel';
// Shared by the bar and table views so the two can't describe the same filter differently.
export const DARK_CHANNEL_TOGGLE_LABEL = `Show dark channels (<${DARK_CHANNEL_SPEND_SHARE_PCT}% of spend in window)`;

// Legend wording and spend-bar dimming shared by the bar view and the drawer's Inter-Channel
// Effects chart, which draw two-stage reallocation the same way. The glossary defines the two
// labels under these exact terms. Added credit is what a demand generation tactic drove that
// showed up in demand capture; reduced credit is what demand capture took that demand generation
// drove.
export const TWO_STAGE_ADDED_LABEL = 'Driven in demand capture';
export const TWO_STAGE_REDUCED_LABEL = 'Driven by demand generation';
export const SPEND_BAR_OPACITY = 0.5;

// A geo model's label for every DMA outside the model, pooled into one market.
export const POOLED_MARKET_GEO = 'other';
export const POOLED_MARKET_LABEL = 'All other markets, combined';
